import { DEFAULT_MAP_SOURCE } from '@env';

import { Injectable } from '@angular/core';

import { EsriMap, IEsriMapOptions } from '@lib/geo/esri';
import { GoogleMap, IGoogleMapOptions } from '@lib/geo/google/map';
import { isEsriMap, isGoogleMap } from '@lib/geo/helpers';

/**
 * Map Factory Service
 *
 * This service provides type-safe map management and type checking for both Google and Esri maps.
 * It eliminates the need for 'any' casting and provides a unified interface for map operations.
 */
@Injectable({
  providedIn: 'root',
})
export class MapFactoryService {
  /**
   * Type guard to check if a map instance is a GoogleMap
   * @param map - The map instance to check
   * @returns True if the map is a GoogleMap instance
   */
  isGoogleMap(map: any): map is GoogleMap {
    return isGoogleMap(map);
  }

  /**
   * Type guard to check if a map instance is an EsriMap
   * @param map - The map instance to check
   * @returns True if the map is an EsriMap instance
   */
  isEsriMap(map: any): map is EsriMap {
    return isEsriMap(map);
  }

  /**
   * Gets the current map source configuration
   * @returns The current map source ('google' or 'esri')
   */
  getCurrentMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  /**
   * Checks if the current map source is Google
   * @returns True if using Google Maps
   */
  isGoogleProvider(): boolean {
    return (DEFAULT_MAP_SOURCE as string) === 'google';
  }

  /**
   * Checks if the current map source is Esri
   * @returns True if using Esri Maps
   */
  isEsriProvider(): boolean {
    return (DEFAULT_MAP_SOURCE as string) === 'esri';
  }

  /**
   * Creates appropriate map options based on the current map source
   * @param baseOptions - Base options to extend
   * @returns Typed options for the current map provider
   */
  createMapOptions(
    baseOptions: Partial<IGoogleMapOptions | IEsriMapOptions>,
  ): IGoogleMapOptions | IEsriMapOptions {
    if (this.isGoogleProvider()) {
      return baseOptions as IGoogleMapOptions;
    } else if (this.isEsriProvider()) {
      return { ...baseOptions, animated: false } as IEsriMapOptions;
    }
    throw new Error(`Unsupported map source: ${DEFAULT_MAP_SOURCE}`);
  }

  /**
   * Safely executes a function based on the map type
   * @param map - The map instance
   * @param googleFn - Function to execute for Google maps
   * @param esriFn - Function to execute for Esri maps
   * @returns The result of the appropriate function
   */
  executeForMapType<T>(
    map: any,
    googleFn: (googleMap: GoogleMap) => T,
    esriFn: (esriMap: EsriMap) => T,
  ): T {
    if (this.isGoogleMap(map)) {
      return googleFn(map);
    } else if (this.isEsriMap(map)) {
      return esriFn(map);
    }
    throw new Error('Unknown map type');
  }
}
